import React, { useState, useEffect, useRef } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { buildHierarchicalClassificationOptions, sortCategoriesHierarchically } from '../../../utils/classificationHierarchy';

const DocumentDetailsSection = ({
  // Classification
  selectedCategory = null,
  onCategoryChange,
  categories = [],
  // Document fields
  documentTitle = '',
  onDocumentTitleChange,
  documentTitleError = '',
  documentRegisteredTo = '',
  onDocumentRegisteredToChange,
  documentRegisteredToError = '',
  documentReferenceNumber = '',
  onDocumentReferenceNumberChange,
  documentReferenceNumberError = '',
  documentIssueDate = '',
  onDocumentIssueDateChange,
  documentExpiryDate = '',
  onDocumentExpiryDateChange,
  remindMeBefore = '',
  onRemindMeBeforeChange,
  documentIssuingAuthority = '',
  onDocumentIssuingAuthorityChange,
  documentIssuingAuthorityError = '',
  otherComments = '',
  onOtherCommentsChange,
  otherCommentsError = '',
  // Show/hide fields
  showClassification = true,
  showRegisteredTo = true,
  showReferenceNumber = true,
  showIssueDate = true,
  showExpiryDate = true,
  showRemindMeBefore = true,
  showIssuingAuthority = true,
  showComments = true,
  // Disable all fields
  disabled = false
}) => {
  const [classificationOptions, setClassificationOptions] = useState([]);
  const issueDateRef = useRef(null);
  const expiryDateRef = useRef(null);

  useEffect(() => {
    // Convert classifications to hierarchical options format for react-select
    const classificationsToUse = Array.isArray(categories) && categories.length > 0 ? categories : [];
    
    // Sort hierarchically and build options with hierarchical labels (tree-style)
    const sortedCategories = sortCategoriesHierarchically(classificationsToUse);
    const options = buildHierarchicalClassificationOptions(sortedCategories, {
      showHierarchy: true,
      treeStyle: true
    });
    
    setClassificationOptions(options);
  }, [categories]);

  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#667eea' : '#e9ecef',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(102, 126, 234, 0.25)' : 'none',
      minHeight: '49px',
      height: '49px',
      borderRadius: '1rem',
      '&:hover': {
        borderColor: '#ced4da'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#667eea' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#495057',
      '&:active': {
        backgroundColor: '#667eea'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6c757d'
    })
  };

  // Remind me before options
  const remindMeBeforeOptions = [
    { value: '1day', label: '1 day before' },
    { value: '2days', label: '2 days before' },
    { value: '3days', label: '3 days before' },
    { value: '1week', label: '1 week before' },
    { value: '2weeks', label: '2 weeks before' },
    { value: '1month', label: '1 month before' }
  ];

  return (
    <div className="mb-4">
      <h5 className="mb-3" style={{ color: '#495057', fontWeight: 600, borderBottom: '2px solid #e9ecef', paddingBottom: '8px' }}>
        Document Details
      </h5>
      <Row>
        {/* Document Classification */}
        {showClassification && (
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-label">
                Document Classification
              </Form.Label>
              <Select
                options={classificationOptions}
                value={(() => {
                  const selectedOption = classificationOptions.find(opt => opt.value === selectedCategory);
                  if (selectedOption) {
                    return { ...selectedOption, label: selectedOption.originalLabel || selectedOption.label };
                  }
                  return null;
                })()}
                onChange={(option) => !disabled && onCategoryChange && onCategoryChange(option ? option.value : null)}
                placeholder="Select document classification"
                isClearable
                isSearchable
                isDisabled={disabled}
                getOptionLabel={(option) => option.originalLabel || option.label}
                formatOptionLabel={({ label }) => (
                  <div style={{ whiteSpace: 'pre' }}>{label}</div>
                )}
                styles={customStyles}
              />
            </Form.Group>
          </Col>
        )}

        {/* Document Title */}
        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="text-label">
              Document Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={documentTitle}
              onChange={(e) => !disabled && onDocumentTitleChange && onDocumentTitleChange(e.target.value)}
              placeholder="Enter document title"
              required
              disabled={disabled}
              className={documentTitleError ? 'is-invalid' : ''}
            />
            {documentTitleError && <div className="text-danger small">{documentTitleError}</div>}
          </Form.Group>
        </Col>

        {/* Document Registered To */}
        {showRegisteredTo && (
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-label">
                Document Registered to
              </Form.Label>
              <Form.Control
                type="text"
                value={documentRegisteredTo}
                onChange={(e) => !disabled && onDocumentRegisteredToChange && onDocumentRegisteredToChange(e.target.value)}
                placeholder="Company name, person name, etc"
                disabled={disabled}
                className={documentRegisteredToError ? 'is-invalid' : ''}
              />
              {documentRegisteredToError && <div className="text-danger small">{documentRegisteredToError}</div>}
            </Form.Group>
          </Col>
        )}

        {/* Document Reference Number */}
        {showReferenceNumber && (
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-label">
                Document Reference Number
              </Form.Label>
              <Form.Control
                type="text"
                value={documentReferenceNumber}
                onChange={(e) => !disabled && onDocumentReferenceNumberChange && onDocumentReferenceNumberChange(e.target.value)}
                placeholder="Trade License number, Passport number, etc"
                disabled={disabled}
                className={documentReferenceNumberError ? 'is-invalid' : ''}
              />
              {documentReferenceNumberError && <div className="text-danger small">{documentReferenceNumberError}</div>}
            </Form.Group>
          </Col>
        )}

        {/* Document Issue Date */}
        {showIssueDate && (
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-label">
                Document Issue Date
              </Form.Label>
              <Form.Control
                ref={issueDateRef}
                type="date"
                value={documentIssueDate}
                onChange={(e) => !disabled && onDocumentIssueDateChange && onDocumentIssueDateChange(e.target.value)}
                onClick={() => !disabled && issueDateRef.current?.showPicker?.()}
                disabled={disabled}
              />
            </Form.Group>
          </Col>
        )}

        {/* Document Expiry Date */}
        {showExpiryDate && (
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-label">
                Document Expiry Date
              </Form.Label>
              <Form.Control
                ref={expiryDateRef}
                type="date"
                value={documentExpiryDate}
                onChange={(e) => !disabled && onDocumentExpiryDateChange && onDocumentExpiryDateChange(e.target.value)}
                onClick={() => !disabled && expiryDateRef.current?.showPicker?.()}
                disabled={disabled}
              />
            </Form.Group>
          </Col>
        )}

        {/* Remind me before - only show if expiry date is entered */}
        {showRemindMeBefore && documentExpiryDate && (
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-label">
                Remind me before
              </Form.Label>
              <Select
                options={remindMeBeforeOptions}
                value={remindMeBeforeOptions.find(opt => opt.value === remindMeBefore) || null}
                onChange={(option) => !disabled && onRemindMeBeforeChange && onRemindMeBeforeChange(option ? option.value : '')}
                placeholder="Select reminder time"
                isClearable
                isSearchable
                isDisabled={disabled}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                styles={customStyles}
              />
            </Form.Group>
          </Col>
        )}

        {/* Document Issuing Authority */}
        {showIssuingAuthority && (
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="text-label">
                Document Issuing Authority
              </Form.Label>
              <Form.Control
                type="text"
                value={documentIssuingAuthority}
                onChange={(e) => !disabled && onDocumentIssuingAuthorityChange && onDocumentIssuingAuthorityChange(e.target.value)}
                placeholder="Enter issuing authority"
                disabled={disabled}
                className={documentIssuingAuthorityError ? 'is-invalid' : ''}
              />
              {documentIssuingAuthorityError && <div className="text-danger small">{documentIssuingAuthorityError}</div>}
            </Form.Group>
          </Col>
        )}

        {/* Other Comments */}
        {showComments && (
          <Col xs={12}>
            <Form.Group className="mb-3">
              <Form.Label className="text-label">
                Other Comments
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={otherComments}
                onChange={(e) => !disabled && onOtherCommentsChange && onOtherCommentsChange(e.target.value)}
                placeholder="Enter any additional comments"
                disabled={disabled}
                className={otherCommentsError ? 'is-invalid' : ''}
              />
              {otherCommentsError && <div className="text-danger small">{otherCommentsError}</div>}
            </Form.Group>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default DocumentDetailsSection;
